import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgxFileDropModule } from 'ngx-file-drop';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { UpsertUserComponent } from './components/users/components/upsert-user/upsert-user.component';
import { UsersComponent } from './components/users/users.component';
import { IsVisibleForUserDirective } from './core/directives/is-visible-for-user.directive';
import { MapDirective } from './core/directives/map.directive';
import { GeneralInterceptor } from './core/interceptors/general.interceptor';
import { AlertService } from './core/services/alert.service';
import { AuthService } from './core/services/auth.service';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { RolesComponent } from './components/roles/roles.component';
import { UpsertRoleComponent } from './components/roles/components/upsert-role/upsert-role.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { RegisterComponent } from './components/register/register.component';
import { ForgetPasswordComponent } from './components/forget-password/forget-password.component';
import { ActivateAccountComponent } from './components/activate-account/activate-account.component';
import { EventCategoryComponent } from './components/event-category/event-category.component';
import { UpsertEventCategoryComponent } from './components/event-category/upsert-event-category/upsert-event-category.component';
import { VolunteerCategoryComponent } from './components/volunteer-category/volunteer-category.component';
import { UpsertVolunteerCategoryComponent } from './components/volunteer-category/upsert-volunteer-category/upsert-volunteer-category.component';
import { VolunteerRoleTypeComponent } from './components/volunteer-role-type/volunteer-role-type.component';
import { UpsertVolunteerRoleTypeComponent } from './components/volunteer-role-type/upsert-volunteer-role-type/upsert-volunteer-role-type.component';
import { EventComponent } from './components/event/event.component';
import { UpsertEventComponent } from './components/event/upsert-event/upsert-event.component';
import { MyProfileComponent } from './components/my-profile/my-profile.component';
import { ReactiveFormsModule } from '@angular/forms';
import { UpdateMyBasicInformationComponent } from './components/update-my-basic-information/update-my-basic-information.component';
import { UpdateMyRoleSettingsComponent } from './components/update-my-role-settings/update-my-role-settings.component';
import { UpdateMyPasswordComponent } from './components/update-my-password/update-my-password.component';
import { KvkkComponent } from './components/kvkk/kvkk.component';
import { MyEducationListComponent } from './components/my-education-list/my-education-list.component';
import { UpsertEducationInfoComponent } from './components/upsert-education-info/upsert-education-info.component';
import { MyProfessionListComponent } from './components/my-profession-list/my-profession-list.component';
import { UpsertProfessionComponent } from './components/my-profession-list/upsert-profession/upsert-profession.component';
import { TermsAndUsageComponent } from './components/terms-and-usage/terms-and-usage.component';
import { AboutProjectComponent } from './components/about-project/about-project.component';
import { VolunteerComponent } from './components/volunteer/volunteer.component';
import { AnnouncementComponent } from './components/announcement/announcement.component';
import { UpsertAnnouncementComponent } from './components/announcement/upsert-announcement/upsert-announcement.component';
import { MyAnnouncementsComponent } from './components/my-announcements/my-announcements.component';
import { AnnouncementDetailComponent } from './components/announcement-detail/announcement-detail.component';
import { EventPropertyComponent } from './components/event-property/event-property.component';
import { CreateEventPropertyComponent } from './components/event-property/create-event-property/create-event-property.component';
import { UpdateEventPropertyComponent } from './components/event-property/update-event-property/update-event-property.component';
import { MyEventsComponent } from './components/my-events/my-events.component';
import { ApplyForEventComponent } from './components/apply-for-event/apply-for-event.component';
import { ViewProfileComponent } from './components/view-profile/view-profile.component';
import { ApplicationListComponent } from './components/application-list/application-list.component';
import { MyRequestsComponent } from './components/my-requests/my-requests.component';
import { InsertRequestComponent } from './components/my-requests/insert-request/insert-request.component';
import { GetMyRequestComponent } from './components/my-requests/get-my-request/get-my-request.component';
import { UserRequestListComponent } from './components/user-request-list/user-request-list.component';
import { UserRequestDetailComponent } from './components/user-request-list/user-request-detail/user-request-detail.component';
import { JudgeFormComponent } from './components/judge-form/judge-form.component';
import { JudgeFormListComponent } from './components/judge-form-list/judge-form-list.component';
import { UpgradeUserComponent } from './components/upgrade-user/upgrade-user.component';
import { MyApplicationListComponent } from './components/my-application-list/my-application-list.component';
import { RefereeFormComponent } from './components/referee-form/referee-form.component';
import { RefereeFormListComponent } from './components/referee-form-list/referee-form-list.component';

@NgModule({
  declarations: [
    AppComponent,
    UsersComponent,
    MainLayoutComponent,
    LoginComponent,
    UpsertUserComponent,
    IsVisibleForUserDirective,
    MapDirective,
    HomeComponent,
    RolesComponent,
    UpsertRoleComponent,
    ResetPasswordComponent,
    RegisterComponent,
    ForgetPasswordComponent,
    ActivateAccountComponent,
    EventCategoryComponent,
    UpsertEventCategoryComponent,
    VolunteerCategoryComponent,
    UpsertVolunteerCategoryComponent,
    VolunteerRoleTypeComponent,
    UpsertVolunteerRoleTypeComponent,
    EventComponent,
    UpsertEventComponent,
    MyProfileComponent,
    UpdateMyBasicInformationComponent,
    UpdateMyRoleSettingsComponent,
    UpdateMyPasswordComponent,
    KvkkComponent,
    MyEducationListComponent,
    UpsertEducationInfoComponent,
    MyProfessionListComponent,
    UpsertProfessionComponent,
    TermsAndUsageComponent,
    AboutProjectComponent,
    VolunteerComponent,
    AnnouncementComponent,
    UpsertAnnouncementComponent,
    MyAnnouncementsComponent,
    AnnouncementDetailComponent,
    EventPropertyComponent,
    CreateEventPropertyComponent,
    UpdateEventPropertyComponent,
    MyEventsComponent,
    ApplyForEventComponent,
    ViewProfileComponent,
    ApplicationListComponent,
    MyRequestsComponent,
    InsertRequestComponent,
    GetMyRequestComponent,
    UserRequestListComponent,
    UserRequestDetailComponent,
    JudgeFormComponent,
    JudgeFormListComponent,
    UpgradeUserComponent,
    MyApplicationListComponent,
    RefereeFormComponent,
    RefereeFormListComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgSelectModule,
    FormsModule,
    HttpClientModule,
    NgxFileDropModule,
    NgxChartsModule,
    BrowserAnimationsModule,
    ReactiveFormsModule
  ],
  providers: [{
    provide: HTTP_INTERCEPTORS,
    useClass: GeneralInterceptor,
    multi: true
  }, AuthService, AlertService],
  bootstrap: [AppComponent]
})
export class AppModule { }
